import React from "react";
import { getUpdateWebformPromoterInstruction, WebformPromoterFromState } from "./components/model";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { OptionalTextAreaTranslationValueInput, RequiredTranslationValueInput, TranslationValueReadonly } from "./components/TranslationValueInnerForm";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import FormElementBehaviourDropdown, { getFormElementBehaviourLabel } from "components/dropdowns/reactselect/FormElementBehaviourDropdown";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import { nounToLower } from "utilities/translationUtils";
import { removeEmptyTranslationValues } from "utilities/freefieldUtils";

interface WebformPromoterDetailSectionProps {
  webformId: number;
  promoter: server.dto.WebformPromoterFunctionGroup;
}

export const WebformPromoterDetailSection = (props: WebformPromoterDetailSectionProps) => {
  const [formState, setFormState] = React.useState<WebformPromoterFromState>({
    webformId: props.webformId,

    header: props.promoter.header,
    description: props.promoter.description,

    contactAddress: props.promoter.contact.address,
    contactPosition: props.promoter.contact.position,
    contactMobile: props.promoter.contact.mobile,
    contactFacebook: props.promoter.contact.facebook,
    contactNationality: props.promoter.contact.nationality,
    contactDateOfBirth: props.promoter.contact.birthDate,
    contactPassportNumber: props.promoter.contact.passportNumber,
    contactSocialSecurityNumber: props.promoter.contact.socialSecurity,
    contactAdditionalInformation: props.promoter.contact.additional,

    companyAddressLine1: props.promoter.company.address1,
    companyAddressLine2: props.promoter.company.address2,
    companyPostalCode: props.promoter.company.postalCode,
    companyCity: props.promoter.company.city,
    companyPhone: props.promoter.company.phone,
    companyWebsite: props.promoter.company.website,
    companyFacebook: props.promoter.company.facebook,
    companyTaxNumber: props.promoter.company.taxNumber,
    companyBusinessNumber: props.promoter.company.businessRegistrationNumber,
    companyAdditionalInformation: props.promoter.company.additional,
  });

  const onValueChanged = (key: string, value: any) => {
    if (key === "header") {
      setFormState(prevState => ({
        ...prevState,
        header: value
      }));
    } else if (key === "description") {
      setFormState(prevState => ({
        ...prevState,
        description: removeEmptyTranslationValues(value)
      }));
    } else if (key.startsWith("contact")) {
      setFormState(prevState => ({
        ...prevState,
        [key]: value
      }));
    } else if (key.startsWith("company")) {
      setFormState(prevState => ({
        ...prevState,
        [key]: value
      }));
    }
  }

  return <DetailSectionTemplate
    onValueChanged={onValueChanged}
    isInPageEditable
    sections={[{
      key: "promoterDetails",
      title: <span style={{ fontSize: 18 }}>
        {`${site.currentEnvironment.functionGroups.promoter.singular} ${nounToLower(RESX.GeneralLabels.FunctionGroup)}`}
      </span>,
      type: "collapsable",
      fields: [{
        key: "header",
        label: RESX.GeneralLabels.Title,
        render: () => <TranslationValueReadonly values={formState.header} />,
        inPageEdit: {
          render: ({ value, onChange, submit, isSaving }: RenderEditOptions<server.dto.TranslationValue[]>) => <RequiredTranslationValueInput
            submit={submit}
            onChange={onChange}
            values={value}
            isSaving={isSaving}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.header,
          submitChange: (header: server.dto.TranslationValue[]) => window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup({
            ...getUpdateWebformPromoterInstruction(formState),
            header,
          }),
        },
      }, {
        key: "description",
        label: RESX.GeneralLabels.Description,
        isEmpty: formState.description.length === 0,
        render: () => <TranslationValueReadonly values={formState.description} />,
        inPageEdit: {
          render: ({ value, onChange, submit, isSaving }: RenderEditOptions<server.dto.TranslationValue[]>) => <OptionalTextAreaTranslationValueInput
            submit={submit}
            onChange={onChange}
            values={value}
            isSaving={isSaving}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.description,
          submitChange: (description: server.dto.TranslationValue[]) => window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup({
            ...getUpdateWebformPromoterInstruction(formState),
            description,
          }),
        }
      }, {
        key: "companyHeader",
        hideLabel: true,
        label: RESX.Company.resxCompany,
        render: () => <h3>{RESX.Company.resxCompany}</h3>
      }, {
        key: "companyName",
        label: RESX.GeneralLabels.Name,
        render: () => <>{getFormElementBehaviourLabel("visibleMandatory")}</>
      }, {
        key: "companyAddressLine1",
        label: RESX.Address.AddressLine + " 1",
        render: () => <>{getFormElementBehaviourLabel(formState.companyAddressLine1)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyAddressLine1,
          submitChange: async (companyAddressLine1: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              companyAddressLine1,
            })),
        },
      }, {
        key: "companyAddressLine2",
        label: RESX.Address.AddressLine + " 2",
        render: () => <>{getFormElementBehaviourLabel(formState.companyAddressLine2)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyAddressLine2,
          submitChange: async (companyAddressLine2: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              companyAddressLine2,
            })),
        },
      }, {
        key: "companyPostalCode",
        label: RESX.Address.PostalCode,
        render: () => <>{getFormElementBehaviourLabel(formState.companyPostalCode)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyPostalCode,
          submitChange: async (companyPostalCode: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              companyPostalCode,
            })),
        },
      }, {
        key: "companyCity",
        label: RESX.Address.City,
        render: () => <>{getFormElementBehaviourLabel(formState.companyCity)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyCity,
          submitChange: async (companyCity: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              companyCity,
            })),
        },
      }, {
        key: "companyPhone",
        label: RESX.ContactDetails.PhoneNumber,
        render: () => <>{getFormElementBehaviourLabel(formState.companyPhone)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyPhone,
          submitChange: async (companyPhone: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              companyPhone,
            })),
        },
      }, {
        key: "companyWebsite",
        label: RESX.Company.CompanyWebsite,
        render: () => <>{getFormElementBehaviourLabel(formState.companyWebsite)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyWebsite,
          submitChange: async (companyWebsite: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              companyWebsite,
            })),
        },
      }, {
        key: "companyFacebook",
        label: "Facebook",
        render: () => <>{getFormElementBehaviourLabel(formState.companyFacebook)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyFacebook,
          submitChange: async (companyFacebook: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              companyFacebook,
            })),
        },
      }, {
        key: "companyTaxNumber",
        label: RESX.RegistrationNumber.VatNumber,
        render: () => <>{getFormElementBehaviourLabel(formState.companyTaxNumber)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyTaxNumber,
          submitChange: async (companyTaxNumber: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              companyTaxNumber,
            })),
        },
      }, {
        key: "companyBusinessNumber",
        label: RESX.RegistrationNumber.BusinessLicenceNumber,
        render: () => <>{getFormElementBehaviourLabel(formState.companyBusinessNumber)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyBusinessNumber,
          submitChange: async (companyBusinessNumber: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              companyBusinessNumber,
            })),
        },
      }, {
        key: "companyAdditionalInformation",
        label: RESX.Company.AdditionalInformation,
        sublabel: RESX.Webform.FreeTextField,
        render: () => <YesNoText value={formState.companyAdditionalInformation} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={v => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyAdditionalInformation,
          submitChange: async (companyAdditionalInformation: boolean) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              companyAdditionalInformation,
            })),
        }
      }, {
        key: "contactHeader",
        hideLabel: true,
        label: RESX.Contact.resxContact,
        render: () => <h3>{RESX.Contact.resxContact}</h3>
      }, {
        key: "contactName",
        label: RESX.GeneralLabels.Name,
        render: () => <>{getFormElementBehaviourLabel("visibleMandatory")}</>
      }, {
        key: "contactEmail",
        label: RESX.GeneralLabels.Email,
        render: () => <>{getFormElementBehaviourLabel("visibleMandatory")}</>
      }, {
        key: "contactAddress",
        label: RESX.Address.resxAddress,
        render: () => <>{getFormElementBehaviourLabel(formState.contactAddress)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactAddress,
          submitChange: async (contactAddress: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              contactAddress,
            })),
        },
      }, {
        key: "contactPosition",
        label: RESX.Contact.Position,
        render: () => <>{getFormElementBehaviourLabel(formState.contactPosition)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactPosition,
          submitChange: async (contactPosition: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              contactPosition,
            })),
        },
      }, {
        key: "contactMobile",
        label: RESX.Contact.Mobile,
        render: () => <>{getFormElementBehaviourLabel(formState.contactMobile)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactMobile,
          submitChange: async (contactMobile: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              contactMobile,
            })),
        },
      }, {
        key: "contactFacebook",
        label: "Facebook",
        render: () => <>{getFormElementBehaviourLabel(formState.contactFacebook)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactFacebook,
          submitChange: async (contactFacebook: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              contactFacebook,
            })),
        },
      }, {
        key: "contactNationality",
        label: RESX.Passport.Nationality,
        render: () => <>{getFormElementBehaviourLabel(formState.contactNationality)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactNationality,
          submitChange: async (contactNationality: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              contactNationality,
            })),
        },
      }, {
        key: "contactDateOfBirth",
        label: RESX.Contact.DateOfBirth,
        render: () => <>{getFormElementBehaviourLabel(formState.contactDateOfBirth)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactDateOfBirth,
          submitChange: async (contactDateOfBirth: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              contactDateOfBirth,
            })),
        },
      }, {
        key: "contactPassportNumber",
        label: RESX.Passport.PassportNumber,
        render: () => <>{getFormElementBehaviourLabel(formState.contactPassportNumber)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactPassportNumber,
          submitChange: async (contactPassportNumber: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              contactPassportNumber,
            })),
        },
      }, {
        key: "contactSocialSecurityNumber",
        label: RESX.Contact.SocialSecurity,
        render: () => <>{getFormElementBehaviourLabel(formState.contactSocialSecurityNumber)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactSocialSecurityNumber,
          submitChange: async (contactSocialSecurityNumber: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              contactSocialSecurityNumber,
            })),
        },
      }, {
        key: "contactAdditionalInformation",
        label: RESX.Company.AdditionalInformation,
        sublabel: RESX.Webform.FreeTextField,
        render: () => <YesNoText value={formState.contactAdditionalInformation} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={v => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactAdditionalInformation,
          submitChange: async (contactAdditionalInformation: boolean) =>
            window.CoreApi.WebformConfiguration.UpdatePromoterFunctionGroup(getUpdateWebformPromoterInstruction({
              ...formState,
              contactAdditionalInformation,
            })),
        }
      }]
    }]}
  />
}